import { service } from '@ember/service';
import { Ability } from 'ember-can';
import EntryModel from 'garaje/models/entry';
import InviteModel from 'garaje/models/invite';
import type AuthzService from 'garaje/services/authz';
import type CurrentAdminService from 'garaje/services/current-admin';
import type StateService from 'garaje/services/state';
import { Permission } from 'garaje/utils/ui-permissions';
import _intersection from 'lodash/intersection';

import type { GetCanReviewOrReadBlocklistMatchModel } from './security';

type SupportedModelName = 'invite' | 'entry';

export default class VisualComplianceAbility extends Ability {
  @service declare authz: AuthzService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare state: StateService;

  declare model: GetCanReviewOrReadBlocklistMatchModel | undefined;

  // (can "review visual-compliance")
  // (can "review visual-compliance" model=EntryModel|InviteModel)
  get canReview(): boolean {
    const { parent } = this.model ?? {};

    if (parent instanceof EntryModel) return this.canReviewAnEntryApproval;
    if (parent instanceof InviteModel) return this.canReviewAnInviteApproval;

    return this.canReviewAnInviteApproval || this.canReviewAnEntryApproval;
  }

  get canView(): boolean {
    const { parent } = this.model ?? {};

    if (parent instanceof EntryModel) return this.canViewAnEntryApproval;
    if (parent instanceof InviteModel) return this.canViewAnInviteApproval;

    return this.canViewAnInviteApproval || this.canViewAnEntryApproval;
  }

  // (can "review an invite approval for visual-compliance")
  get canReviewAnInviteApproval(): boolean {
    return this.#canReview('invite');
  }

  // (can "review an entry approval for visual-compliance")
  get canReviewAnEntryApproval(): boolean {
    return this.#canReview('entry');
  }

  // (can "view an invite approval for visual-compliance")
  get canViewAnInviteApproval(): boolean {
    return this.#canView('invite');
  }

  // (can "view an entry approval for visual-compliance")
  get canViewAnEntryApproval(): boolean {
    return this.#canView('entry');
  }

  #hasSubscription(): boolean {
    return !!this.state.features?.canAccessVisualCompliance;
  }

  #isContact(): boolean {
    const { currentLocation } = this.state;
    const config = currentLocation?.visualComplianceConfiguration.content;
    const contactIds = (config && config.contactIds) || [];

    return contactIds.map(String).includes(this.currentAdmin.id);
  }

  #canReview(modelName: SupportedModelName): boolean {
    if (!this.#hasSubscription()) return false;
    if (this.#isContact()) return true;

    const permission = {
      invite: Permission.VISITORS_INVITE_VISUAL_COMPLIANCE_APPROVAL_REVIEW,
      entry: Permission.VISITORS_ENTRY_VISUAL_COMPLIANCE_APPROVAL_REVIEW,
    }[modelName];
    return this.authz.hasPermissionAtCurrentLocation(permission);
  }

  #canView(modelName: SupportedModelName): boolean {
    if (!this.#hasSubscription()) return false;
    if (this.#isContact()) return true;

    const permission = {
      invite: Permission.VISITORS_INVITE_VISUAL_COMPLIANCE_APPROVAL_READ,
      entry: Permission.VISITORS_ENTRY_VISUAL_COMPLIANCE_APPROVAL_READ,
    }[modelName];
    return this.authz.hasPermissionAtCurrentLocation(permission);
  }
}
